import { useEffect, useRef, useState } from "react";
import type { StreamChat } from "stream-chat";
import {
  rowsFromStreamChannels,
  sumUnreadExcludingMuted,
} from "@/lib/messages/streamAlertUnread";

/**
 * Mute-aware Stream unread for Firefly / Messenger badges (HOST-4).
 * Recomputes on Stream notification events and when bridge mutes change.
 * Never falls back to `total_unread_count` (includes muted / phantom channels
 * and left the teal halo stuck — dev-sync 5de4f20b).
 */
export function useStreamMuteAwareUnread(
  client: StreamChat | null,
  mutedChannelIds: readonly string[]
): number {
  const [unreadCount, setUnreadCount] = useState(0);
  const mutedKey = mutedChannelIds.join("\0");
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!client) {
      setUnreadCount(0);
      return;
    }

    let cancelled = false;

    const refresh = async () => {
      const uid = client.userID;
      if (!uid) {
        if (!cancelled) setUnreadCount(0);
        return;
      }
      try {
        const channels = await client.queryChannels(
          { members: { $in: [uid] } },
          { last_message_at: -1 },
          { limit: 30, state: true, watch: false }
        );
        if (cancelled) return;
        // Prefer live watched channels when present — countUnread is fresher.
        const byCid = new Map<string, { id?: string | null; cid?: string | null; countUnread: () => number }>();
        for (const ch of channels) {
          const cid = String(ch.cid || "");
          if (!cid) continue;
          byCid.set(cid, {
            id: ch.id,
            cid: ch.cid,
            countUnread: () => ch.countUnread(),
          });
        }
        for (const ch of Object.values(client.activeChannels || {})) {
          const cid = String(ch.cid || "");
          if (!cid) continue;
          byCid.set(cid, {
            id: ch.id,
            cid: ch.cid,
            countUnread: () => ch.countUnread(),
          });
        }
        const rows = rowsFromStreamChannels([...byCid.values()]);
        setUnreadCount(sumUnreadExcludingMuted(rows, mutedChannelIds));
      } catch {
        if (!cancelled) setUnreadCount(0);
      }
    };

    const scheduleRefresh = () => {
      if (typeof window === "undefined") {
        void refresh();
        return;
      }
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        void refresh();
      }, 120);
    };

    void refresh();
    const subs = [
      client.on("notification.message_new", scheduleRefresh),
      client.on("notification.mark_read", scheduleRefresh),
      client.on("notification.mark_unread", scheduleRefresh),
      client.on("message.read", scheduleRefresh),
      client.on("connection.changed", scheduleRefresh),
      client.on("channel.truncated", scheduleRefresh),
    ];

    return () => {
      cancelled = true;
      if (timerRef.current != null && typeof window !== "undefined") {
        window.clearTimeout(timerRef.current);
      }
      for (const sub of subs) sub.unsubscribe();
    };
    // mutedKey stands in for mutedChannelIds contents without identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, mutedKey]);

  return unreadCount;
}
